/**
 * Disease Data Module
 * 
 * Contains all diseases from the campaign's disease table with
 * functionality for random disease selection with rarity weighting.
 */

export interface Disease {
    name: string;
    rarity: 'Common' | 'Uncommon' | 'Rare';
    contraction: string;
    dc: number;
    incubation: string;
    damage: string;
    symptoms: string;
    special: string;
}

/**
 * All diseases from diseases.csv
 */
export const DISEASES: Disease[] = [
    {
        name: 'Coral Scratch',
        rarity: 'Common',
        contraction: 'Injury',
        dc: 12,
        incubation: '1d4 hours',
        damage: '1d3 Dex',
        symptoms: 'The wound site develops a distinctive crusty, calcified edge with tiny white polyp-like growths. The affected area becomes stiff and inflexible, with the skin taking on a rough, coral-like texture. Victims experience sharp, stabbing pains that worsen with movement, as if tiny limestone shards are grinding within the wound. The surrounding tissue swells and becomes hot to the touch, often weeping a clear, salty discharge.',
        special: 'HP damage from exposure to coral does not heal naturally until the character recovers or is cured.'
    },
    {
        name: 'Sea Rot',
        rarity: 'Uncommon',
        contraction: 'Contact',
        dc: 20,
        incubation: '1 day',
        damage: '1d6 Con, 1d6 Str',
        symptoms: "The victim's flesh literally begins to soften and rot while they still live. It starts with a foul, fishy odor that grows progressively worse, attracting flies even at sea. The skin takes on a greenish-gray tinge and becomes disturbingly soft and spongy. Black discoloration appears around injuries and spreads slowly outward. In advanced cases, the flesh becomes so degraded that it can be pressed with a finger, leaving deep indentations. Victims often report feeling their own tissues \"melting\" inside their bodies, accompanied by intense weakness and muscle wasting.",
        special: ''
    },
    {
        name: 'Sea Sores',
        rarity: 'Common',
        contraction: 'Ingested',
        dc: 14,
        incubation: '1d4 days',
        damage: '1d4 Str, 1d2 Cha',
        symptoms: "Begin as small, itchy blisters inside the mouth and throat that quickly spread to cover the lips, gums, and tongue. These lesions burst and weep a foul-smelling, yellowish fluid that makes eating and drinking agonizing. The victim's breath becomes noticeably putrid, and their voice grows hoarse and raspy. Advanced cases show open, weeping sores spreading down the neck and across the face, with the victim drooling constantly and unable to swallow without intense pain.",
        special: ''
    },
    {
        name: 'Suntouch',
        rarity: 'Uncommon',
        contraction: 'Exposure',
        dc: 18,
        incubation: '1 day',
        damage: '1d6 Wis, 1d2 Con',
        symptoms: "The victim becomes increasingly detached from reality, their eyes taking on a distant, unfocused glaze as if perpetually staring at the horizon. They mutter incomprehensibly about water, the sea calling to them, drowning in air. Their skin becomes dry and cracked despite adequate water intake, developing a leathery, sun-damaged appearance. Victims grow obsessed with the ocean, constantly trying to reach the water or drink seawater. They lose track of time and often don't recognize friends or shipmates, speaking to them as if they were strangers or sea spirits.",
        special: 'Characters reduced to 0 Wis by suntouch are rendered insane instead of comatose, acquiring an overpowering compulsion to immerse themselves in the sea and/or drink seawater.'
    },
    {
        name: 'Bluerot',
        rarity: 'Rare',
        contraction: 'Injury',
        dc: 20,
        incubation: '1 minute',
        damage: '1d8 Str or 1d8 Con if Str is 0',
        symptoms: "The victim's skin takes on a distinct bluish-purple discoloration, starting at the extremities and creeping inward toward the core. The flesh becomes cold to the touch, corpse-like, as if already dead. Water seems to bead on the skin rather than being absorbed. The victim's eyes cloud over with a milky, cataract-like film. They move sluggishly, as if underwater, their speech slurred and bubbling. A distinctive smell of brine and decay follows them. As the disease progresses, the victim seems to be transforming into something aquatic and undead—their humanity literally draining away.",
        special: 'An afflicted creature that dies from bluerot rises as a drowned one at the next sunset.'
    },
    {
        name: 'Filth Fever',
        rarity: 'Common',
        contraction: 'Injury',
        dc: 12,
        incubation: '1d3 days',
        damage: '1d3 Dex, 1d3 Con',
        symptoms: "Begins with violent chills and teeth-chattering shivers despite warm conditions. The victim develops a high fever with drenching night sweats that soak through bedding and clothing. Joints become painfully swollen and stiff, particularly in the fingers and knees, making movement difficult. A distinctive sour, unwashed smell emanates from the victim's skin. Muscles tremble with weakness, and the victim becomes increasingly clumsy and uncoordinated.",
        special: ''
    },
    {
        name: 'Shakes',
        rarity: 'Common',
        contraction: 'Contact',
        dc: 13,
        incubation: '1 day',
        damage: '1d8 Dex',
        symptoms: 'Victims develop an uncontrollable tremor that begins in the hands and spreads throughout the body. Even at rest, fingers twitch and dance involuntarily. Simple tasks like holding a cup or tying knots become nearly impossible. The trembling intensifies with any attempt at fine motor control, causing spilled drinks, dropped tools, and illegible handwriting. In severe cases, the victim\'s entire body quakes visibly, their teeth chattering and head nodding rhythmically.',
        special: ''
    },
    {
        name: 'Slimy Doom',
        rarity: 'Common',
        contraction: 'Contact',
        dc: 14,
        incubation: '1 day',
        damage: '1d4 Con',
        symptoms: "The victim's skin becomes perpetually damp and clammy, covered in a thin layer of greasy, foul-smelling slime that cannot be washed away. This secretion leaves a glistening trail wherever they sit or lie, and makes gripping weapons or tools nearly impossible. The victim's eyes water constantly, nose runs continuously, and they frequently cough up thick, viscous phlegm. Their flesh takes on a pallid, grayish hue and feels unnaturally soft and spongy to the touch.",
        special: ''
    },
    {
        name: 'Darkrot',
        rarity: 'Rare',
        contraction: 'Injury',
        dc: 12,
        incubation: '1 day',
        damage: '1d6 Dex',
        symptoms: 'A wound that should heal instead festers and spreads. The flesh around the injury turns black as pitch, radiating outward in dark tendrils like spilled ink. The affected area becomes numb and cold, developing a shiny, necrotic appearance. A foul, gangrenous stench emanates from the wound. The blackened tissue is clearly dead, hanging loose from the body. In severe cases, the rot spreads so extensively that the affected limb becomes a withered, blackened thing barely attached to the body—surgical removal being the only option to save the victim\'s life.',
        special: 'A failed save against damage requires the character to make a second save to avoid losing a limb. Roll 1d4: 1 left leg, 2 right leg, 3 left arm, 4 right arm.'
    },
    {
        name: 'Scaleflake',
        rarity: 'Common',
        contraction: 'Contact',
        dc: 15,
        incubation: '1d3 days',
        damage: '1d4 Cha',
        symptoms: "The skin develops patches of thick, dry scales that flake away constantly, leaving a trail of skin debris wherever the victim goes. These scales are initially small but grow larger and more pronounced, often appearing in distinctive patterns across the body. The affected areas itch intensely, and scratching causes the scales to peel away in sheets, revealing raw, weeping skin beneath. The victim's appearance becomes increasingly reptilian and unsettling, with scales sometimes forming along the face and neck.",
        special: ''
    },
    {
        name: 'Dripskin',
        rarity: 'Rare',
        contraction: 'Contact',
        dc: 14,
        incubation: '1 day',
        damage: '1d3 Con',
        symptoms: "Perhaps the most disturbing disease, the victim's skin literally begins to liquefy and drip away. It starts as patches of skin that become translucent and watery, weeping a clear fluid that pools beneath the victim. These patches spread and merge, causing sheets of skin to slough off, dissolving into puddles of flesh-colored liquid. The victim leaves wet, skin-textured stains everywhere they sit or sleep. Remarkably, this process is not immediately painful, but the psychological horror is immense. The victim can watch their own skin melting away, revealing raw muscle and tissue beneath. These puddles remain infectious and must be carefully cleaned.",
        special: 'Puddles of skin left behind by the victim remain contagious for a week.'
    },
    {
        name: 'Shaking Fever',
        rarity: 'Common',
        contraction: 'Contact',
        dc: 13,
        incubation: '2 days',
        damage: '1d4 Dex',
        symptoms: "Combines the tremors of the Shakes with intense fever. Victims alternate between burning hot and freezing cold, their bodies wracked with violent shivering fits. The trembling is so severe during fever spikes that victims often bite their tongues or cheeks bloody. Sweat pours from their skin during hot phases, only to turn icy cold moments later. The victim's eyes develop a glassy, unfocused appearance, and they often mumble deliriously during the worst episodes.",
        special: ''
    },
    {
        name: 'Spotted Plague',
        rarity: 'Common',
        contraction: 'Contact',
        dc: 16,
        incubation: '1 day',
        damage: '1d4 Cha',
        symptoms: "Distinctive purple-black spots appear across the skin, starting on the torso and spreading outward to the limbs and face. These spots are raised and tender to the touch, often weeping clear fluid or blood. The victim develops a persistent, hacking cough that produces bloody sputum. Lymph nodes swell visibly in the neck, armpits, and groin, forming painful lumps. The victim's appearance becomes increasingly haggard and corpse-like, with sunken eyes and pallid skin between the spots.",
        special: ''
    },
    {
        name: 'Bilge Rot',
        rarity: 'Common',
        contraction: 'Injury',
        dc: 13,
        incubation: '1d4 days',
        damage: '1d4 Con',
        symptoms: "Begins with a persistent, nauseating stench emanating from the victim's breath and skin—a smell of stagnant water, decay, and mildew that clings to everything they touch. The victim develops a wet, rattling cough that brings up dark, foul-smelling mucus. Their extremities become puffy and waterlogged-looking, with skin that dimples when pressed and is slow to return to normal. Digestive problems cause constant nausea and frequent vomiting of bile-green fluid.",
        special: ''
    },
    {
        name: 'The Vanishing',
        rarity: 'Rare',
        contraction: 'Exposure',
        dc: 15,
        incubation: '1 day',
        damage: '1d6 Cha',
        symptoms: "The victim slowly fades from existence. It begins subtly—their skin becoming slightly translucent, as if they're made of thin parchment. Day by day, they grow more transparent and ghostly. Colors fade from their skin, hair, and eyes. They become increasingly difficult to see, especially in dim light or against certain backgrounds. The victim can see this happening, watching their own hand become see-through, their body disappearing piece by piece. Unlike their fading flesh, their clothing and equipment remain fully visible, creating the eerie image of empty clothes moving about the ship. In final stages, only a barely-perceptible shimmer in the air remains.",
        special: 'Once a creature takes Charisma damage, it turns translucent, becoming increasingly transparent and taking damage with each passing day. Equipment is not rendered transparent by the disease. When Charisma is reduced to half or less, the creature gains a +4 circumstance bonus on Hide checks. A creature reduced to 0 Cha fades away, leaving behind any items worn or held. Cannot be passed from one creature to another.'
    },
    {
        name: 'Hadozee Flu',
        rarity: 'Common',
        contraction: 'Contact',
        dc: 15,
        incubation: '1 day',
        damage: '1d6 Str, 1d4 Con',
        symptoms: "Patches of fuzzy, colorful fungal growth erupt across the victim's skin—yellows, oranges, and browns—particularly in warm, moist areas like armpits, groin, and between toes. The fungus spreads in circular patterns, leaving the skin beneath red, inflamed, and intensely itchy. Scratching provides momentary relief but causes the spores to spread further and can lead to bleeding sores. The victim develops a persistent cough as fungal spores colonize their lungs, producing a distinctive musty, earthy smell with each breath. Their sputum becomes discolored with visible fungal material. In advanced cases, thick fungal plaques form on the skin that must be scraped away, and the victim's fingernails and toenails become discolored, thickened, and crumbly as the fungus invades them. The constant itching and respiratory distress leave victims exhausted and desperate, their strength literally being consumed by the parasitic growth.",
        special: ''
    },
    {
        name: 'The Voices',
        rarity: 'Rare',
        contraction: 'Exposure',
        dc: 13,
        incubation: '1 minute',
        damage: '1d4 Wis',
        symptoms: "The victim begins hearing whispers—at first just at the edge of perception, like distant conversations in another room. These voices grow louder and more insistent, speaking in languages the victim doesn't know yet somehow understands. They mutter dark prophecies, horrible truths, maddening riddles. The victim's eyes develop an empty, hollow look as they cock their head to listen to sounds only they can hear. They respond to questions that weren't asked, argue with invisible speakers, laugh at jokes no one told. Most disturbing are moments when they suddenly go rigid, eyes wide, mouth working silently, as if something is speaking through them. In the final stages, they claw at their own skull, screaming that something is trying to get out—and they're horrifyingly right.",
        special: "All saves are Will saves, not Fortitude. If Wis drops to 0, subject's brain claws out of its skull as an intellect devourer 1d4 hours later."
    },
    {
        name: 'Softpox',
        rarity: 'Uncommon',
        contraction: 'Contact',
        dc: 28,
        incubation: '1 day',
        damage: '-1 to natural armor (or 10 hp if +0)',
        symptoms: "The victim's natural defenses literally soften and weaken. Scales (if present) become rubbery and pliable, shell-like skin turns spongy, and even normal flesh loses its resilience. What should be firm becomes yielding—the victim's body develops an unsettling softness, as if the underlying structure is dissolving. Bruises appear with the slightest touch, and even minor impacts leave deep, lasting indentations in the flesh. The victim feels vulnerable and exposed, their body unable to maintain its proper form and rigidity.",
        special: ''
    },
    {
        name: 'Dune Fever',
        rarity: 'Uncommon',
        contraction: 'Injury',
        dc: 15,
        incubation: '3d6 days',
        damage: '1d6 Con',
        symptoms: "A cyclical nightmare that follows a predictable, terrible pattern. The victim experiences distinct phases: first come the bone-deep chills, where they shiver uncontrollably despite blankets and warm conditions, teeth chattering so hard they chip. Then the fever spike hits—temperature soaring, skin burning to the touch, eyes glazed and rolling. Drenching sweats follow, soaking through everything. This cycle repeats every two to three days with clockwork regularity. Between episodes, the victim appears haggard and exhausted, skin with a distinctive yellow-gray pallor. The spleen often swells visibly, creating a painful bulge in the abdomen. Victims suffer from splitting headaches, joint pain, and waves of nausea. In severe cases, they may babble deliriously during fever spikes, or develop a disturbing symptom where their skin takes on a sallow, almost jaundiced appearance.",
        special: ''
    },
    {
        name: 'Stinksweat',
        rarity: 'Uncommon',
        contraction: 'Injury',
        dc: 13,
        incubation: '1d4 hours',
        damage: '-',
        symptoms: "The victim begins producing a truly horrific sweat—not just unpleasant, but genuinely nauseating. This oily, yellow-tinged perspiration carries a stench so powerful it causes retching in anyone nearby. The smell is indescribable: rotting fish mixed with sulfur and spoiled milk. Worse, the sweat seems to affect the victim's mind, causing confusion and disorientation. They stumble about randomly, unable to maintain a straight course, their eyes unfocused and movements erratic. The victim appears drunk or poisoned, wandering aimlessly while leaving a trail of stomach-turning odor.",
        special: 'Victim moves in a random direction at no faster than base speed each round and suffers a -2 penalty to attack rolls and saving throws until the disease runs its course.'
    }
];

/**
 * Get diseases filtered by rarity
 */
export function getDiseasesByRarity(rarity: Disease['rarity']): Disease[] {
    return DISEASES.filter(d => d.rarity === rarity);
}

/**
 * Roll a random disease with weighted rarity:
 * - 60% chance of Common
 * - 30% chance of Uncommon  
 * - 10% chance of Rare
 */
export function rollRandomDisease(): Disease {
    const roll = Math.random() * 100;

    let rarity: Disease['rarity'];
    if (roll < 60) {
        rarity = 'Common';
    } else if (roll < 90) {
        rarity = 'Uncommon';
    } else {
        rarity = 'Rare';
    }

    const diseasesOfRarity = getDiseasesByRarity(rarity);
    const randomIndex = Math.floor(Math.random() * diseasesOfRarity.length);

    return diseasesOfRarity[randomIndex];
}

/**
 * Format a disease for display in the outcome generator context
 */
export function formatDiseaseForContext(disease: Disease): string {
    let context = `ROLLED DISEASE: ${disease.name} (${disease.rarity})
Contraction: ${disease.contraction}, DC: ${disease.dc}
Incubation: ${disease.incubation}, Damage: ${disease.damage}
Symptoms: ${disease.symptoms}`;

    if (disease.special) {
        context += `\nSpecial: ${disease.special}`;
    }

    return context;
}
